const HTML_TAG_PATTERN = /<[a-z][\s\S]*>/i;

export function looksLikeHtml(content: string) {
  return HTML_TAG_PATTERN.test(content);
}

export function stripHtml(content: string) {
  return content
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isRichTextEmpty(content: string) {
  return stripHtml(content).length === 0;
}

/**
 * Tags the rich text editor can produce. Everything else is unwrapped to its
 * text, and every attribute is dropped, so stored HTML written by one manager
 * cannot run script or load anything when another member views it.
 */
const ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "UL", "OL", "LI", "P", "BR", "DIV"]);
const DROPPED_WITH_CONTENT = new Set(["SCRIPT", "STYLE", "TEMPLATE", "IFRAME", "OBJECT", "EMBED"]);

function sanitizeNode(source: Node, target: Node, doc: Document) {
  for (const child of Array.from(source.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent ?? ""));
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    const tag = (child as Element).tagName;
    if (DROPPED_WITH_CONTENT.has(tag)) continue;
    if (ALLOWED_TAGS.has(tag)) {
      const clean = doc.createElement(tag.toLowerCase());
      sanitizeNode(child, clean, doc);
      target.appendChild(clean);
    } else {
      sanitizeNode(child, target, doc);
    }
  }
}

export function sanitizeRichText(html: string): string {
  // DOMParser builds an inert document: nothing in it runs or loads.
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const output = document.implementation.createHTMLDocument("");
  const container = output.createElement("div");
  sanitizeNode(parsed.body, container, output);
  return container.innerHTML;
}
